// File: src/components/Invoices/OperationalTimeline.jsx

import React from "react";
import {
  Upload,
  ScanLine,
  Link2,
  ClipboardCheck,
  PackageCheck,
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
} from "lucide-react";

function iconForType(type) {
  const safe = String(type || "").toLowerCase();

  if (safe.includes("upload") || safe.includes("created")) return Upload;
  if (safe.includes("ocr") || safe.includes("scan") || safe.includes("extract")) return ScanLine;
  if (safe.includes("match") || safe.includes("link")) return Link2;
  if (safe.includes("approval") || safe.includes("approved")) return ClipboardCheck;
  if (safe.includes("receive") || safe.includes("goods")) return PackageCheck;
  if (safe.includes("payment") || safe.includes("paid")) return CreditCard;
  if (safe.includes("reject")) return XCircle;

  return CheckCircle;
}

function statusClass(type) {
  const safe = String(type || "").toLowerCase();

  if (safe.includes("reject") || safe.includes("failed")) {
    return {
      line: "border-red-400/30",
      icon: "border-red-400/30 bg-red-500/10 text-red-300",
      badge: "border-red-400/30 bg-red-500/10 text-red-300",
    };
  }

  if (safe.includes("pending") || safe.includes("waiting")) {
    return {
      line: "border-amber-400/30",
      icon: "border-amber-400/30 bg-amber-500/10 text-amber-300",
      badge: "border-amber-400/30 bg-amber-500/10 text-amber-300",
    };
  }

  return {
    line: "border-green-400/25",
    icon: "border-green-400/30 bg-green-500/10 text-green-300",
    badge: "border-green-400/30 bg-green-500/10 text-green-300",
  };
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function buildFallbackTimeline(invoice) {
  const rows = [];

  if (invoice?.uploaded_at || invoice?.created_at) {
    rows.push({
      type: "uploaded",
      title: "Invoice created / uploaded",
      at: invoice.uploaded_at || invoice.created_at,
      actor: invoice.uploaded_by || null,
      note: invoice.notes || null,
    });
  }

  if (invoice?.approved_at) {
    rows.push({
      type: "approved",
      title: "Invoice approved",
      at: invoice.approved_at,
      actor: invoice.approved_by || null,
      note: invoice.approved_comment || null,
    });
  }

  if (invoice?.rejected_at) {
    rows.push({
      type: "rejected",
      title: "Invoice rejected",
      at: invoice.rejected_at,
      actor: invoice.rejected_by || null,
      note: invoice.rejection_comment || null,
    });
  }

  if (invoice?.paid_at) {
    rows.push({
      type: "paid",
      title: "Invoice paid",
      at: invoice.paid_at,
      actor: invoice.paid_by || null,
      note: null,
    });
  }

  return rows;
}

export default function OperationalTimeline({ timeline = [], invoice = null }) {
  const rows = Array.isArray(timeline) && timeline.length
    ? timeline
    : buildFallbackTimeline(invoice);

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Operational Timeline</h2>
          <p className="mt-1 text-sm text-gray-400">
            Full audit trail of what happened to this invoice from creation to payment.
          </p>
        </div>

        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-gray-300">
          <Clock className="h-4 w-4" />
          {rows.length} events
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-amber-400/20 bg-amber-500/10 p-4 text-sm text-amber-300">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            No timeline activity found for this invoice yet.
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((row, index) => {
            const Icon = iconForType(row.type || row.title);
            const styles = statusClass(row.type || row.status || row.title);

            return (
              <div key={`${row.type || "event"}-${row.at || index}-${index}`} className="relative">
                {index !== rows.length - 1 && (
                  <div
                    className={`absolute left-[18px] top-10 h-[calc(100%+1rem)] border-l ${styles.line}`}
                  />
                )}

                <div className="flex gap-4">
                  <div
                    className={`z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${styles.icon}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  <div className="flex-1 rounded-xl border border-white/10 bg-black/25 p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-semibold text-white">
                          {row.title || row.action_title || "Invoice activity"}
                        </div>

                        <div className="mt-1 text-xs text-gray-400">
                          {formatDate(row.at || row.created_at)}
                          {row.actor || row.actor_name ? ` · ${row.actor || row.actor_name}` : ""}
                        </div>
                      </div>

                      <span
                        className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${styles.badge}`}
                      >
                        {String(row.type || row.status || "event").replaceAll("_", " ").toUpperCase()}
                      </span>
                    </div>

                    {(row.note || row.action_message || row.message) && (
                      <div className="mt-3 text-sm text-gray-300">
                        {row.note || row.action_message || row.message}
                      </div>
                    )}

                    {row.stage && (
                      <div className="mt-2 text-xs text-gray-500">
                        Stage: {row.stage}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}